import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import "./Auth.scss";

import { Box, Grid, Checkbox } from "@mui/material";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import Field from "./../../component/Field";
import Label from "./../../component/Label";
import Toaster from "../../component/Toaster";
import SelectField from "../../component/SelectField";
import ActionButton from "../../component/ActionButton";
import { LocalStorage, Product } from "../../util/Utils";

import { useAppDispatch } from "../../store/hooks";
import { loginAsync } from "../../store/loginSlice";
import { roleAction } from "../../store/roleAccess/roleAccessSlice";

import favIconGa from "../../assets/images/ga-favicon.png";
import favIconSip from "../../assets/images/sip-favicon.png";
import favIconCrico from "../../assets/images/crico/favicon.png";

import AuthLayout from "./../auth-layout";
import navigationFaUserAndFaAdmin from "../../service/navigation";
import { THEME } from "../../util/LookupConst";
import { decryptData, encryptData } from "../../service/EncriptDecript";

type initialType = {
  rememberMe: boolean;
  centerCode: string;
  userId: string;
  password: string;
  productCode: any;
};

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [actionProgress, setActionProgress] = useState(false);
  const ref = useRef(true);
  const [localLanguage, setLocalLanguage] = useState("en");

  const dispatch = useAppDispatch();

  let rememberMeDetailsDecripted = LocalStorage.get("rememberMeDetails");
  const rememberMeDetails = decryptData(rememberMeDetailsDecripted);

  const languages = [
    { value: "en", label: t("languages.english") },
    { value: "hi", label: t("languages.hindi") },
    { value: "ta", label: t("languages.tamil") },
    { value: "ml", label: t("languages.malayalam") },
  ];

  const initialValue = {
    centerCode: rememberMeDetails?.centerCode || "",
    userId: rememberMeDetails?.userId || "",
    password: window.atob(rememberMeDetails?.password || ""),
    rememberMe: rememberMeDetails?.rememberMe === "true" ? true : false,
    productCode: "",
  };

  const label = { inputProps: { "aria-label": "Checkbox" } };
  let login = true;

  //get rols access
  const getRoleAccessById = (roleId: number) => {
    dispatch(roleAction({ id: roleId, apiAction: "list-role-access" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          LocalStorage.append("menuAccess", result?.payload.data.data);
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        // This is kept for debug the app if any error occurred in DEV
        console.log(err);
      });
  };

  useEffect(() => {
    if (ref.current) {
      ref.current = false;
      dynamicFavIcon();
      dynamicPageTitle();
      let userToken = sessionStorage.getItem("user-token");
      if (!userToken) {
        userToken = LocalStorage.get("user-token");
      }
      if (userToken) {
        navigationFaUserAndFaAdmin(navigate);
      }
    }
  });

  // Dynamic Fav Icon
  const dynamicFavIcon = () => {
    let link: any = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href =
      Product.ProductCode() === THEME.GA
        ? favIconGa
        : Product.ProductCode() === THEME.SA
        ? favIconSip
        : favIconCrico;
  };

  const dynamicPageTitle = () => {
    Product.ProductCode() === THEME.SA
      ? (document.title = "SA --> SIP SABG LOGIN")
      : Product.ProductCode() === THEME.GA
      ? (document.title = "GA --> SIP SABG LOGIN")
      : (document.title = "CRICO --> SIP SABG LOGIN");
  };

  const handleLangChange = (e: any) => {
    setLocalLanguage(e.target.value);
    i18n.changeLanguage(e.target.value);
  };

  const handleSubmit = (values: initialType) => {
    values.productCode =
      Product.ProductCode() && Product.ProductCode()?.toUpperCase();

    setActionProgress(true);
    values.centerCode = values.centerCode.toUpperCase();
    dispatch(loginAsync(values))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          if (!result?.payload?.data?.data?.token) {
            Toaster.error(
              undefined,
              "Unable to login. Please try after some time."
            );
            return;
          }
          if (result?.payload?.data?.data?.roleId)
            getRoleAccessById(result?.payload?.data?.data?.roleId);
          LocalStorage.remove("unauthorized");
          LocalStorage.append("user-token", result?.payload?.data?.data?.token);
          LocalStorage.append(
            "loginType",
            result?.payload?.data?.data?.loginType
          );
          LocalStorage.append(
            "centerTypeId",
            result?.payload?.data?.data?.franchisee?.centerTypeId
          );
          LocalStorage.append(
            "taxType",
            result?.payload?.data?.data?.franchisee?.taxType
          );
          LocalStorage.append("menus", result?.payload?.data?.data?.menus);
          LocalStorage.append("isLoggedIn", true);

          // Remember Me checkbox is checked, the information entered in the data field labelled as “Center Code“, “User Name“ and “Password“ will be saved.
          if (values?.rememberMe) {
            const sensitiveInfo = {
              userId: values.userId,
              password: window.btoa(values.password),
              centerCode: values.centerCode,
              rememberMe: values.rememberMe?.toString(),
            };
            const encrypted = encryptData(sensitiveInfo);
            
            LocalStorage.append("rememberMeDetails", encrypted);
          } else {
            LocalStorage.remove("rememberMeDetails");
          }

          navigationFaUserAndFaAdmin(navigate);
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        console.log("error - ", err);
      })
      .finally(() => {
        setActionProgress(false);
      });
  };

  return (
    <Box>
      <Grid
        container
        rowSpacing={1}
        className="center main login-module-background"
      >
        <Grid item xs={10} md={10} lg={8}>
          <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            className="viewport-height center"
          >
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={6}
              className="login-animation-container theme-bg"
            >
              <AuthLayout />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              lg={6}
              className="login-form-container corner-mask"
            >
              <div className="language-setting">
                <span>
                  <SelectField
                    name="userTypeId"
                    value={localLanguage}
                    onChange={(e: any) => {
                      handleLangChange(e);
                    }}
                    placeholder={t("enterHere")}
                    inputProps={{ "aria-label": "Without label" }}
                    options={languages}
                  />
                </span>
              </div>
              <div className="login-padding">
                <div className="logo center">
                  <span className="login-module-brand-icon"></span>
                </div>
                {login && (
                  <Formik
                    initialValues={initialValue}
                    onSubmit={handleSubmit}
                    validationSchema={Yup.object().shape({
                      centerCode: Yup.string().required(
                        `${t("login.centerCode")} ${t("is required")}`
                      ),
                      userId: Yup.string().required(
                        `${t("login.userId")} ${t("is required")}`
                      ),
                      password: Yup.string()
                        .required(`${t("login.password")} ${t("is required")}`)
                        .min(8, "Password must be at least 8 characters"),
                    })}
                  >
                    {({
                      values,
                      handleChange,
                      handleBlur,
                      errors,
                      touched,
                    }: any) => {
                      return (
                        <Form>
                          <div className="flex-container">
                            <Label title={t("login.centerCode")} />
                            <div className="flex">
                              <div
                                className={
                                  "text-icon " +
                                  (errors.centerCode && touched.centerCode
                                    ? "error-border"
                                    : "")
                                }
                              >
                                <span className="centercode-control-icon"></span>
                              </div>

                              <div className="text-container border-radius">
                                <Field
                                  name="centerCode"
                                  placeholder={t("enterHere")}
                                  value={values.centerCode}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  enterkey="true"
                                  error={
                                    errors.centerCode && touched.centerCode
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          <div className="flex-container">
                            <Label title={t("login.userId")} />
                            <div className="flex">
                              <div
                                className={
                                  "text-icon " +
                                  (errors.userId && touched.userId
                                    ? "error-border"
                                    : "")
                                }
                              >
                                <span className="userId-control-icon"></span>
                              </div>
                              <div className="text-container border-radius">
                                <Field
                                  id="userId"
                                  name="userId"
                                  placeholder={t("enterHere")}
                                  value={values.userId}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.userId && touched.userId}
                                  enterkey="true"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="flex-container">
                            <Label title={t("login.password")} />
                            <div className="flex">
                              <div
                                className={
                                  "text-icon " +
                                  (errors.password && touched.password
                                    ? "error-border"
                                    : "")
                                }
                              >
                                <span className="password-control-icon"></span>
                              </div>
                              <div className="text-container border-radius">
                                <Field
                                  defaultValue={
                                    rememberMeDetails?.password || ""
                                  }
                                  id="password"
                                  name="password"
                                  placeholder={t("enterHere")}
                                  type="password"
                                  error={errors.password && touched.password}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  enterkey="true"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="forget-password">
                            <div>
                              <label
                                onClick={() => navigate("/forgot-password/otp")}
                              >
                                {t("forgotPassword")}
                              </label>
                            </div>
                            <div>
                              <div>
                                <Checkbox
                                  style={{ padding: "0px" }}
                                  {...label}
                                  checked={values.rememberMe}
                                  name="rememberMe"
                                  onChange={handleChange}
                                />{" "}
                                <label>{t("rememberMe")}</label>
                              </div>
                            </div>
                          </div>
                          <ActionButton
                            label={t("signIn")}
                            loading={actionProgress}
                          />
                          <div style={{ marginBottom: "23vh" }}></div>
                        </Form>
                      );
                    }}
                  </Formik>
                )}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
export default Login;
